import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { USER_API, toastMsg, FEATURE_STATUS_RERUNNING } from "config";
import {
  normalizeId,
  handleToast,
  instanceOfStripboardTableDayBreak,
  getApiErrorToastMessage,
} from "utils";
import { invalidateScheduleQueries, scheduleKeys, projectKeys } from "../../queries";
import { useMemo, useRef } from "react";
import { useAxios } from "../useAxios";
import {
  extractDaybreakPatchPayload,
  mergeDaybreakInStripboardCache,
} from "./mergeDaybreakInStripboardCache";
import {
  UpdateDayBreakIndexParams,
  UpdateBannerIndexParams,
  UpdateDayBreakLockParams,
  UpdateDayBreakDateParams,
  CreateManualDaybreakParams,
  CreateBannerParams,
  MoveStripboardToBoneyardParams,
  MoveBoneyardToStripBoardParams,
  DeleteDayBreakParams,
  DeleteBannerParams,
  DuplicateStripboardParams,
  UpdateStripboardParams,
  ReRunSchedulingParams,
  CreateAutomaticDayBreaksParams,
} from "./useStripboard.types";

export type ScheduleRerunGuard = {
  blocked: boolean;
  onBlocked: () => void;
};

interface UseStripboardMutationsParams {
  projectId: string | number;
  documentId: string | number;
  /** When `blocked`, `reRunScheduling.mutate` / `mutateAsync` call `onBlocked` instead of the API. */
  scheduleRerunGuard?: ScheduleRerunGuard;
}

/** The draft a stripboard mutation started in. Its request, rollback and refresh all use it. */
type StripboardScope = { pid: string; did: string };
type Scoped<TVariables> = TVariables & { scope: StripboardScope };

/**
 * The mutation with `mutate` and `mutateAsync` that send this render's draft along with the
 * variables. The schedule pages stay mounted across a draft switch, and React Query hands an
 * in-flight mutation each new render's callbacks, so every callback reads the draft from
 * `variables.scope`, never from the render. Both stay stable while the draft stays the same.
 */
function useScopedMutation<TData, TError, TVariables, TContext>(
  mutation: UseMutationResult<TData, TError, Scoped<TVariables>, TContext>,
  scope: StripboardScope
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { mutate, mutateAsync } = mutation;
  const scoped = useMemo(
    () => ({
      mutate: (
        variables: TVariables,
        options?: Parameters<UseMutationResult<TData, TError, TVariables, TContext>["mutate"]>[1]
      ) => mutate({ ...variables, scope }, options),
      mutateAsync: (
        variables: TVariables,
        options?: Parameters<
          UseMutationResult<TData, TError, TVariables, TContext>["mutateAsync"]
        >[1]
      ) => mutateAsync({ ...variables, scope }, options),
    }),
    [mutate, mutateAsync, scope]
  );
  return { ...mutation, ...scoped };
}

export const useStripboardMutations = ({
  projectId,
  documentId,
  scheduleRerunGuard,
}: UseStripboardMutationsParams) => {
  const queryClient = useQueryClient();
  const axios = useAxios({ allResponse: false });
  const pid = normalizeId(projectId);
  const did = normalizeId(documentId);
  const scope = useMemo<StripboardScope>(() => ({ pid, did }), [pid, did]);
  const scheduleRerunGuardRef = useRef(scheduleRerunGuard);
  scheduleRerunGuardRef.current = scheduleRerunGuard;

  const invalidateSchedule = (target: StripboardScope) =>
    invalidateScheduleQueries(queryClient, target.pid, target.did);

  const updateDayBreakIndexMutation = useMutation({
    mutationFn: ({ scope: target, ...params }: Scoped<UpdateDayBreakIndexParams>): Promise<any> =>
      axios.patch(USER_API.updateOrDeleteDaybreak(target.pid, target.did, params.day_break_id), {
        index: params.new_index,
      }),
    onSuccess: (_data, { scope: target }) => {
      void invalidateSchedule(target);
    },
    onError: () => {
      handleToast(toastMsg.genericServerErr, "error");
    },
  });

  const updateBannerIndexMutation = useMutation({
    mutationFn: ({ scope: target, ...params }: Scoped<UpdateBannerIndexParams>): Promise<any> =>
      axios.patch(USER_API.updateOrDeleteBanner(target.pid, target.did, params.banner_id), {
        index: params.new_index,
      }),
    onSuccess: (_data, { scope: target }) => {
      void invalidateSchedule(target);
    },
    onError: () => {
      handleToast(toastMsg.genericServerErr, "error");
    },
  });

  const updateDayBreakDateMutation = useMutation({
    mutationFn: ({
      scope: target,
      ...params
    }: Scoped<UpdateDayBreakDateParams>): Promise<unknown> =>
      axios.patch(
        USER_API.updateOrDeleteDaybreak(target.pid, target.did, params.daybreak_id.toString()),
        { date: params.date }
      ),
    onMutate: async (variables) => {
      const key = scheduleKeys.stripboard(variables.scope.pid, variables.scope.did);
      await queryClient.cancelQueries({ queryKey: key });
      const previousData = queryClient.getQueryData(key);

      queryClient.setQueryData(key, (old: unknown) => {
        if (!Array.isArray(old)) return old;
        return old.map((item) => {
          if (
            instanceOfStripboardTableDayBreak(item) &&
            item.daybreak_id === variables.daybreak_id
          ) {
            return { ...item, date: variables.date };
          }
          return item;
        });
      });

      return { previousData };
    },
    onError: (_error, { scope: target }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          scheduleKeys.stripboard(target.pid, target.did),
          context.previousData
        );
      }
      handleToast(toastMsg.manualDayBreak.error, "error");
    },
    onSuccess: (response, variables) => {
      const { scope: target } = variables;
      const updatedDaybreak = extractDaybreakPatchPayload(response);
      if (updatedDaybreak) {
        mergeDaybreakInStripboardCache(queryClient, target.pid, target.did, updatedDaybreak);
        void invalidateScheduleQueries(queryClient, target.pid, target.did, {
          includeStripboard: false,
        });
      } else {
        void invalidateSchedule(target);
      }
      if (!variables.silentSuccessToast) {
        handleToast(toastMsg.manualDayBreak.success.update, "success");
      }
    },
  });

  const createManualDaybreakMutation = useMutation({
    mutationFn: ({
      scope: target,
      ...params
    }: Scoped<CreateManualDaybreakParams>): Promise<unknown> =>
      axios.post(USER_API.createManualDaybreak(target.pid, target.did), params),
    onSuccess: async (_data, variables) => {
      await invalidateSchedule(variables.scope);
      if (!variables.silentSuccessToast) {
        handleToast(toastMsg.manualDayBreak.success.create, "success");
      }
    },
    onError: () => {
      handleToast(toastMsg.manualDayBreak.error, "error");
    },
  });

  const createBannerMutation = useMutation({
    mutationFn: ({ scope: target, ...params }: Scoped<CreateBannerParams>): Promise<unknown> =>
      axios.post(USER_API.createBanner(target.pid, target.did), params),
    onSuccess: async (_data, { scope: target }) => {
      await invalidateSchedule(target);
      handleToast(toastMsg.banner.success.create, "success");
    },
    onError: () => {
      handleToast(toastMsg.genericServerErr, "error");
    },
  });

  const updateDayBreakLockMutation = useMutation({
    mutationFn: ({ scope: target, ...params }: Scoped<UpdateDayBreakLockParams>): Promise<any> => {
      return axios.patch(
        USER_API.updateOrDeleteDaybreak(target.pid, target.did, params.daybreak_id.toString()),
        { locked: params.locked }
      );
    },
    onMutate: async (variables) => {
      const key = scheduleKeys.stripboard(variables.scope.pid, variables.scope.did);
      await queryClient.cancelQueries({ queryKey: key });
      const previousData = queryClient.getQueryData(key);

      queryClient.setQueryData(key, (old: any) => {
        if (!old) {
          return old;
        }
        return old.map((item: any) => {
          if (item.daybreak_id === variables.daybreak_id) {
            return { ...item, locked: variables.locked };
          }
          return item;
        });
      });

      return { previousData };
    },
    onError: (error, { scope: target }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          scheduleKeys.stripboard(target.pid, target.did),
          context.previousData
        );
      }
    },
    onSuccess: (response, { scope: target }) => {
      const updatedDaybreak = extractDaybreakPatchPayload(response);
      if (updatedDaybreak) {
        mergeDaybreakInStripboardCache(queryClient, target.pid, target.did, updatedDaybreak);
      }
      void invalidateSchedule(target);
    },
  });

  const duplicateStripboardMutation = useMutation({
    mutationFn: ({
      scope: target,
      ...params
    }: Scoped<DuplicateStripboardParams>): Promise<unknown> =>
      axios.post(USER_API.duplicateStripboard(target.pid, target.did, params.stripboard_id)),
    onSuccess: async (_data, { scope: target }) => {
      await invalidateSchedule(target);
      handleToast(toastMsg.stripboard.duplicateScene.success, "success");
    },
    onError: () => {
      handleToast(toastMsg.genericServerErr, "error");
    },
  });

  const moveStripboardToBoneyardMutation = useMutation({
    mutationFn: ({
      scope: target,
      ...params
    }: Scoped<MoveStripboardToBoneyardParams>): Promise<any> =>
      axios.patch(USER_API.moveStripboardToBoneyard(target.pid, target.did, params.stripboard_id)),
    onSuccess: (_data, { scope: target }) => {
      void invalidateSchedule(target);
    },
  });

  const moveBoneyardToStripBoardMutation = useMutation({
    mutationFn: ({
      scope: target,
      ...params
    }: Scoped<MoveBoneyardToStripBoardParams>): Promise<any> =>
      axios.patch(USER_API.moveBoneyardToStripBoard(target.pid, target.did, params.stripboard_id)),
    onSuccess: (_data, { scope: target }) => {
      void invalidateSchedule(target);
    },
  });

  const deleteDayBreakMutation = useMutation({
    mutationFn: ({ scope: target, ...params }: Scoped<DeleteDayBreakParams>): Promise<any> =>
      axios.delete(USER_API.updateOrDeleteDaybreak(target.pid, target.did, params.day_break_id)),
    onSuccess: (_data, { scope: target }) => {
      void invalidateSchedule(target);
    },
    onError: () => {
      handleToast(toastMsg.genericServerErr, "error");
    },
  });

  const deleteBannerMutation = useMutation({
    mutationFn: ({ scope: target, ...params }: Scoped<DeleteBannerParams>): Promise<any> =>
      axios.delete(
        USER_API.updateOrDeleteBanner(target.pid, target.did, params.stripoard_banner_id)
      ),
  });

  const updateStripboardMutation = useMutation<unknown, Error, Scoped<UpdateStripboardParams>>({
    mutationFn: ({ scope: target, ...params }) =>
      axios.patch(
        USER_API.updateStripboard(target.pid, target.did, params.stripboard_id),
        params.update_data
      ),
    onSuccess: (_data, variables) => {
      if (!variables.deferScheduleInvalidation) {
        void invalidateSchedule(variables.scope);
      }
    },
    onError: (_error, variables) => {
      if (variables.suppressErrorToast) {
        return;
      }
      handleToast(toastMsg.genericServerErr, "error");
    },
  });

  const reRunSchedulingMutation = useMutation({
    mutationFn: ({ scope: target, ...params }: Scoped<ReRunSchedulingParams>): Promise<any> =>
      axios.post(USER_API.reRunScheduling(target.pid, target.did), params, {
        headers: {
          "Idempotency-Key": globalThis.crypto.randomUUID(),
        },
      }),
    onMutate: async ({ scope: target }) => {
      await queryClient.cancelQueries({ queryKey: projectKeys.detail(target.pid) });
      const previousProject = queryClient.getQueryData(projectKeys.detail(target.pid));

      queryClient.setQueryData(projectKeys.detail(target.pid), (old: any) => {
        if (!old) return old;

        let updatedFeatureStatuses =
          old.feature_statuses?.map((fs: any) => {
            if (fs.feature === "schedule") {
              return { ...fs, status: FEATURE_STATUS_RERUNNING };
            }
            return fs;
          }) ?? [];
        const hasSchedule = updatedFeatureStatuses.some((fs: any) => fs.feature === "schedule");
        if (!hasSchedule) {
          updatedFeatureStatuses = [
            ...updatedFeatureStatuses,
            {
              feature_status_id: 0,
              feature: "schedule",
              status: FEATURE_STATUS_RERUNNING,
              metadata: null,
            },
          ];
        }

        return {
          ...old,
          feature_statuses: updatedFeatureStatuses,
        };
      });

      return { previousProject };
    },
    onSuccess: (_data, { scope: target }) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(target.pid) });
      void invalidateSchedule(target);
    },
    onError: (error: unknown, { scope: target }, context) => {
      if (context?.previousProject) {
        queryClient.setQueryData(projectKeys.detail(target.pid), context.previousProject);
      }
      handleToast(
        getApiErrorToastMessage(error, toastMsg.reRunScheduling.error.failedToStartReRun),
        "error"
      );
    },
  });

  const createAutomaticDayBreaksMutation = useMutation({
    mutationFn: ({
      scope: target,
      ...params
    }: Scoped<CreateAutomaticDayBreaksParams>): Promise<any> =>
      axios.post(USER_API.createAutomatedDaybreaks(target.pid, target.did), params),
    onSuccess: (_data, { scope: target }) => {
      void invalidateSchedule(target);
      handleToast(toastMsg.createAutomaticDayBreaks.success, "success");
    },
    onError: () => {
      handleToast(toastMsg.createAutomaticDayBreaks.error.default, "error");
    },
  });

  const updateDayBreakIndex = useScopedMutation(updateDayBreakIndexMutation, scope);
  const updateBannerIndex = useScopedMutation(updateBannerIndexMutation, scope);
  const updateDayBreakDate = useScopedMutation(updateDayBreakDateMutation, scope);
  const createManualDaybreak = useScopedMutation(createManualDaybreakMutation, scope);
  const createBanner = useScopedMutation(createBannerMutation, scope);
  const updateDayBreakLock = useScopedMutation(updateDayBreakLockMutation, scope);
  const duplicateStripboard = useScopedMutation(duplicateStripboardMutation, scope);
  const moveStripboardToBoneyard = useScopedMutation(moveStripboardToBoneyardMutation, scope);
  const moveBoneyardToStripBoard = useScopedMutation(moveBoneyardToStripBoardMutation, scope);
  const deleteDayBreak = useScopedMutation(deleteDayBreakMutation, scope);
  const deleteBanner = useScopedMutation(deleteBannerMutation, scope);
  const updateStripboard = useScopedMutation(updateStripboardMutation, scope);
  const scopedReRunScheduling = useScopedMutation(reRunSchedulingMutation, scope);
  const createAutomaticDayBreaks = useScopedMutation(createAutomaticDayBreaksMutation, scope);

  const updateDaybreakLockDebounce = updateDayBreakLock.mutate;

  const reRunScheduling = useMemo(() => {
    const base = scopedReRunScheduling;
    return {
      ...base,
      mutate: (variables: ReRunSchedulingParams, options?: Parameters<typeof base.mutate>[1]) => {
        const g = scheduleRerunGuardRef.current;
        if (g?.blocked) {
          g.onBlocked();
          return;
        }
        base.mutate(variables, options);
      },
      mutateAsync: async (
        variables: ReRunSchedulingParams,
        options?: Parameters<typeof base.mutateAsync>[1]
      ) => {
        const g = scheduleRerunGuardRef.current;
        if (g?.blocked) {
          g.onBlocked();
          throw new Error("SCHEDULE_RERUN_BLOCKED");
        }
        return base.mutateAsync(variables, options);
      },
    };
  }, [scopedReRunScheduling]);

  return useMemo(
    () => ({
      updateDayBreakIndex,
      updateBannerIndex,
      updateDayBreakDate,
      createManualDaybreak,
      createBanner,
      updateDayBreakLock,
      updateDaybreakLockDebounce,
      duplicateStripboard,
      moveStripboardToBoneyard,
      moveBoneyardToStripBoard,
      deleteDayBreak,
      deleteBanner,
      updateStripboard,
      reRunScheduling,
      createAutomaticDayBreaks,
    }),
    [
      updateDayBreakIndex,
      updateBannerIndex,
      updateDayBreakDate,
      createManualDaybreak,
      createBanner,
      updateDayBreakLock,
      updateDaybreakLockDebounce,
      duplicateStripboard,
      moveStripboardToBoneyard,
      moveBoneyardToStripBoard,
      deleteDayBreak,
      deleteBanner,
      updateStripboard,
      reRunScheduling,
      createAutomaticDayBreaks,
    ]
  );
};
